import { Outlet, useLoaderData, type LoaderFunctionArgs } from "react-router";
import { Trans } from "@lingui/react/macro";
import { readMeetupIntent } from "~/utils/meetup-intent.server";

export function loader({ request }: LoaderFunctionArgs) {
  return { meetup: readMeetupIntent(request) };
}
import React from "react";
import LeftHandSide from "~/components/authentication/left-hand-side";

// The card names the event the attendee scanned into, matching the "Welcome to
// the meetup" screen in web.space. The name comes from the signed meetup intent
// (HMAC-verified in readMeetupIntent, minted server-side from the event record),
// so it is the same organiser-authored title shown everywhere else, rendered as
// escaped text. The brand name is only a fallback.
// eslint-disable-next-line lingui/no-unlocalized-strings -- brand name, not translatable UI copy
const MEETUP_FALLBACK_TITLE = "Mindstone AI meetup";

export default function Auth() {
  const { meetup } = useLoaderData<typeof loader>();
  return (
    <div className="h-[100dvh] flex items-center justify-center">
      <div className="h-full w-full flex items-stretch lg:grid lg:grid-cols-2">
        <div className="hidden lg:block h-full">
          <div className="w-full h-full">
            <LeftHandSide />
          </div>
        </div>
        <div className="h-full w-full overflow-y-auto bg-background">
          {/* Both this element and its scroll parent were h-full, so nothing
              could scroll and justify-center pushed the taller meetup layout
              above the top edge. min-h-full lets it grow and scroll. Written
              as two complete literal strings: this app's classNames is a plain
              join with no tailwind-merge, so a conflicting appended utility
              would resolve by stylesheet order. */}
          <div className={meetup
            ? "w-full min-h-full flex flex-col justify-center items-center relative px-8 py-[72px]"
            : "w-full h-full flex flex-col justify-center items-center relative px-8 py-[72px]"}>
            {meetup && (
              <section aria-label={meetup.name || MEETUP_FALLBACK_TITLE} className="w-full max-w-[400px] mb-8 rounded-lg border border-border p-4 break-words">
                <p className="font-semibold">{meetup.name || MEETUP_FALLBACK_TITLE}</p>
                <p className="text-sm text-muted-foreground">{meetup.location}</p>
                <time dateTime={meetup.startDatetime} className="text-sm text-muted-foreground">
                  {new Date(meetup.startDatetime).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: meetup.timezone ?? "UTC" })} ({meetup.timezone ?? "UTC"})
                </time>
                <p className="text-sm mt-2"><Trans>You’ll return to this event after signing in.</Trans></p>
              </section>
            )}
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
